import { FC } from 'react';
import { CandidateReferralData } from '$types/candidates';
import { ReferralStatus } from '$constants/ReferralStatus';

const STATUS_BADGE_STYLES: { [key: string]: string } = {
  [ReferralStatus.COMPLETED]: 'bg-[#BFEFEC] text-[#016F68]',
  [ReferralStatus.PENDING]: 'bg-[#FCDAC4] text-[#A44101]',
};

function getRatingVariant(value: number | string): 'high' | 'medium' | 'low' {
  if (typeof value === 'number') {
    if (value >= 8) return 'high';
    if (value >= 5) return 'medium';
    return 'low';
  }

  switch (value) {
    case 'Excellent':
      return 'high';
    case 'Very Good':
    case 'Neutral':
      return 'medium';
    default:
      return 'low';
  }
}

const RATING_BADGE_STYLES = {
  high: 'bg-[#BFEFEC] text-[#016F68]',
  medium: 'bg-[#CFD0FF] text-[#2C30D7]',
  low: 'bg-[#FCDAC4] text-[#A44101]',
};

const RatingBadge = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) => {
  if (value === undefined || value === null || value === '') return null;

  const variant = getRatingVariant(value);
  return (
    <div
      className={`text-[10px] py-1 px-2 rounded-md ${RATING_BADGE_STYLES[variant]}`}
    >
      <span className="font-bold">{label}:</span>{' '}
      {typeof value === 'number' ? `${value}/10` : value}
    </div>
  );
};

RatingBadge.defaultProps = {
  value: undefined,
};

const AutomatedReferences: FC<{ references: CandidateReferralData[] }> = ({
  references,
}) => (
  <div className="flex flex-col gap-4">
    {references.map((reference) => (
      <div
        key={reference.Id}
        className="flex flex-col gap-3 rounded border border-solid border-gray-200 p-4"
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex flex-col text-brand-darkgrey">
            <span className="text-sm font-bold">
              {reference.ReferralName || reference.ReferralEmail}
            </span>
            {reference.ReferralRelationship && (
              <span className="text-xs">{reference.ReferralRelationship}</span>
            )}
          </div>
          <span
            className={`text-[10px] font-bold py-1 px-2 rounded-md whitespace-nowrap ${
              STATUS_BADGE_STYLES[reference.Status] ??
              'bg-gray-200 text-gray-600'
            }`}
          >
            {reference.Status}
          </span>
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
          <span>
            <span className="font-bold">Email:</span> {reference.ReferralEmail}
          </span>
          {reference.PositionHeldByCandidate && (
            <span>
              <span className="font-bold">Position:</span>{' '}
              {reference.PositionHeldByCandidate}
            </span>
          )}
          {reference.WouldHireAgain !== null &&
            reference.WouldHireAgain !== undefined && (
              <span>
                <span className="font-bold">Would hire again:</span>{' '}
                {reference.WouldHireAgain ? 'Yes' : 'No'}
              </span>
            )}
          {reference.CreateTime && (
            <span>
              <span className="font-bold">Requested:</span>{' '}
              {new Date(reference.CreateTime).toLocaleDateString()}
            </span>
          )}
        </div>

        {reference.Status === ReferralStatus.COMPLETED ? (
          <div className="flex flex-col gap-3 text-sm text-gray-600">
            {reference.CandidateResponsibilities && (
              <div className="flex flex-col">
                <span className="font-bold">Recommendation</span>
                <span className="whitespace-pre-wrap">
                  {reference.CandidateResponsibilities}
                </span>
              </div>
            )}
            {reference.CandidatePerformance && (
              <div className="flex flex-col">
                <span className="font-bold">Job Performance</span>
                <span className="whitespace-pre-wrap">
                  {reference.CandidatePerformance}
                </span>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <RatingBadge
                label="Likelihood to Recommend"
                value={reference.LikelihoodToRecommend}
              />
              <RatingBadge
                label="Reliability"
                value={reference.CandidateReliability}
              />
              <RatingBadge
                label="Work Ethic"
                value={reference.CandidateWorkEthic}
              />
              <RatingBadge
                label="Collaboration"
                value={reference.CandidateCollaboration}
              />
            </div>
          </div>
        ) : (
          <span className="text-xs italic text-gray-500">
            Awaiting response from reference
          </span>
        )}
      </div>
    ))}
  </div>
);

export default AutomatedReferences;
